import {
  Radar,
  PlusCircle,
  List,
  LogOut,
  Building2,
  Settings,
  Users,
  HelpCircle,
  Coins,
  ShieldCheck,
  Languages,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { NavLink } from "@/components/NavLink";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCredits } from "@/contexts/CreditsContext";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
} from "@/components/ui/sidebar";

const navItems = [
  { key: "newSearch", url: "/search/new", icon: PlusCircle },
  { key: "jobs", url: "/jobs", icon: List },
  { key: "help", url: "/help", icon: HelpCircle },
] as const;

// Company-scoped admin nav (profile.role === "admin"): intra-tenant
// actions — member invites, password resets, company settings.
const adminNavItems = [
  { key: "settings", url: "/settings", icon: Settings },
  { key: "members", url: "/settings/members", icon: Users },
] as const;

// Platform-scoped owner nav (profile.is_owner === true, backed by
// OWNER_USER_IDS Railway env): internal tools. CST202: "translations"
// was previously mis-placed in adminNavItems, leaking to every company
// admin.
const ownerNavItems = [
  { key: "translations", url: "/admin/translations", icon: Languages },
] as const;

export function AppSidebar() {
  const { t } = useTranslation("common");
  const location = useLocation();
  const { profile, logout } = useAuth();
  const { balance, isLoading: creditsLoading } = useCredits();

  const initials = profile
    ? `${(profile.first_name || "")[0] || ""}${(profile.last_name || "")[0] || ""}`.toUpperCase() ||
      "?"
    : "?";

  return (
    <Sidebar className="border-r-0">
      <SidebarHeader className="p-5 border-b border-sidebar-border">
        <NavLink
          to="/"
          className="flex items-center gap-2.5 no-underline"
          activeClassName=""
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-sidebar-primary/20">
            <Radar className="h-5 w-5 text-sidebar-primary" />
          </div>
          <div>
            <h1 className="text-base font-bold tracking-tight text-sidebar-accent-foreground">
              Lead Scout
            </h1>
            <p className="text-[11px] text-sidebar-muted">{t("tagline")}</p>
          </div>
        </NavLink>
      </SidebarHeader>

      <SidebarContent className="px-3 py-4">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.url;
                return (
                  <SidebarMenuItem key={item.key}>
                    <SidebarMenuButton asChild>
                      <NavLink
                        to={item.url}
                        end
                        data-testid={`sidebar-nav-${item.key}`}
                        className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                          isActive
                            ? "bg-sidebar-accent text-sidebar-primary font-medium"
                            : "text-sidebar-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground"
                        }`}
                        activeClassName=""
                      >
                        <item.icon className="h-4 w-4 shrink-0" />
                        <span>{t(`sidebar.${item.key}`)}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {profile?.is_owner && (
          <SidebarGroup>
            <SidebarGroupContent>
              <p className="px-3 pt-2 pb-1 text-[11px] font-medium uppercase tracking-wider text-sidebar-muted">
                {t("sidebar.sectionPlatform")}
              </p>
              <SidebarMenu className="space-y-1">
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to="/admin"
                      end
                      data-testid="sidebar-nav-adminDashboard"
                      className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                        location.pathname === "/admin"
                          ? "bg-sidebar-accent text-sidebar-primary font-medium"
                          : "text-sidebar-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground"
                      }`}
                      activeClassName=""
                    >
                      <ShieldCheck className="h-4 w-4 shrink-0" />
                      <span>{t("sidebar.adminDashboard")}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                {ownerNavItems.map((item) => {
                  const isActive = location.pathname === item.url;
                  return (
                    <SidebarMenuItem key={item.key}>
                      <SidebarMenuButton asChild>
                        <NavLink
                          to={item.url}
                          end
                          data-testid={`sidebar-nav-${item.key}`}
                          className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                            isActive
                              ? "bg-sidebar-accent text-sidebar-primary font-medium"
                              : "text-sidebar-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground"
                          }`}
                          activeClassName=""
                        >
                          <item.icon className="h-4 w-4 shrink-0" />
                          <span>{t(`sidebar.${item.key}`)}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {profile?.role === "admin" && (
          <SidebarGroup>
            <SidebarGroupContent>
              <p className="px-3 pt-2 pb-1 text-[11px] font-medium uppercase tracking-wider text-sidebar-muted">
                {t("sidebar.sectionAdmin")}
              </p>
              <SidebarMenu className="space-y-1">
                {adminNavItems.map((item) => {
                  const isActive = location.pathname === item.url;
                  return (
                    <SidebarMenuItem key={item.key}>
                      <SidebarMenuButton asChild>
                        <NavLink
                          to={item.url}
                          end
                          data-testid={`sidebar-nav-${item.key}`}
                          className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                            isActive
                              ? "bg-sidebar-accent text-sidebar-primary font-medium"
                              : "text-sidebar-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground"
                          }`}
                          activeClassName=""
                        >
                          <item.icon className="h-4 w-4 shrink-0" />
                          <span>{t(`sidebar.${item.key}`)}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="p-3">
        <Separator className="mb-3" />
        {profile?.company_id && (
          <Link
            to="/credits"
            className="flex items-center gap-2 px-2 mb-3 rounded-lg py-1.5 transition-colors hover:bg-sidebar-accent/60 no-underline"
          >
            <Coins className="h-4 w-4 shrink-0 text-sidebar-primary" />
            <span className="text-sm text-sidebar-foreground">
              {creditsLoading ? (
                "…"
              ) : (
                <>
                  <span className="font-semibold">{balance}</span>{" "}
                  {t("sidebar.credits")}
                </>
              )}
            </span>
          </Link>
        )}
        {profile && (
          <div className="flex items-center gap-3 px-2">
            <Avatar className="h-8 w-8 shrink-0">
              <AvatarFallback className="bg-sidebar-primary/10 text-sidebar-primary text-xs font-medium">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-sidebar-accent-foreground truncate">
                {profile.first_name} {profile.last_name}
              </p>
              {profile.company_name && (
                <p className="text-[11px] text-sidebar-muted flex items-center gap-1 truncate">
                  <Building2 className="h-3 w-3 shrink-0" />
                  {profile.company_name}
                </p>
              )}
            </div>
            <LanguageSwitcher />
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0 text-sidebar-muted hover:text-sidebar-accent-foreground"
              onClick={logout}
              title={t("sidebar.signOut")}
              aria-label={t("sidebar.signOut")}
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
